import { type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { endpoints, type DashboardBadges } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";

interface NavItem {
  to: string;
  label: string;
  // badgeKey points at one field of DashboardBadges. When the count > 0,
  // a small chip renders next to the nav item. Undefined = no badge.
  badgeKey?: keyof DashboardBadges;
}

// v2.2.5 — sidebar reorg 12 → 8.  Discovery/Keywords merged under "竞品发现"
// with tabs; System/Audit/X-accounts/Settings merged under "系统" with tabs.
// v2.3.2 — CSV 导入 folded into 频道库 顶栏 (top-right button), this slot
// removed so the nav is the promised 8 items.
const nav: NavItem[] = [
  { to: "/", label: "概览" },
  { to: "/evaluation", label: "评估中心", badgeKey: "evaluation_hub" },
  { to: "/channels", label: "频道库" },
  { to: "/entities", label: "Entities" },
  { to: "/sponsorships", label: "推广审核", badgeKey: "sponsorship_review" },
  { to: "/discovery", label: "竞品发现", badgeKey: "discovery" },
  { to: "/plans", label: "投放筛选" },
  { to: "/campaigns", label: "投放管理" },
  { to: "/exchanges", label: "交易所" },
  { to: "/entities/self", label: "Bitbase 自身" },
  { to: "/system", label: "系统", badgeKey: "x_accounts" },
];

export default function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: () => endpoints.logout(),
    onSuccess: () => navigate("/login", { replace: true }),
  });

  // 30s polling. Failures are silent — the chip just doesn't render. We
  // don't want a network blip to make the whole nav red. retry: false so
  // we don't hammer a failing endpoint.
  const badgesQ = useQuery({
    queryKey: ["dashboard", "badges"],
    queryFn: () => endpoints.dashboardBadges(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border bg-background print:hidden">
        <div className="flex h-14 items-center gap-6 px-6">
          <Link to="/" className="font-semibold">
            Bitbase KOL
          </Link>
          <nav className="flex flex-1 items-center gap-1">
            {nav.map((item) => {
              // v2.6.18 — DashboardBadges now has non-numeric fields
              // (youtube_cookies_dead boolean) so we narrow to number
              // before the count comparison. Non-numeric badgeKeys would
              // never be referenced from the nav anyway.
              const raw = item.badgeKey ? badgesQ.data?.[item.badgeKey] : 0;
              const count = typeof raw === "number" ? raw : 0;
              // v2.1.8 — x_accounts badge is an alert (dead cookies need
              // operator action). Use rose color to differentiate from
              // routine "work to do" badges (sky blue).
              const isAlert = item.badgeKey === "x_accounts";
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors",
                    pathname === item.to
                      ? "bg-muted font-medium"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  {item.label}
                  {count > 0 && (
                    <span
                      className={cn(
                        "inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] font-medium",
                        isAlert
                          ? "bg-rose-100 text-rose-700"
                          : "bg-sky-100 text-sky-700",
                      )}
                      title={isAlert ? "失效 cookies 数,需要重新登录补齐" : undefined}
                    >
                      {count > 99 ? "99+" : count}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          {/* v2.6.18 — YouTube cookies status pill. Shows only when the
              cookies_health_check cron has reported the probe failed.
              Hover for the last yt-dlp error so the operator knows
              whether to refresh cookies or investigate something else. */}
          {badgesQ.data?.youtube_cookies_dead && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-xs font-medium text-rose-700"
              title={badgesQ.data.youtube_cookies_reason ?? "yt-dlp 用 cookies 拉视频失败"}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500"></span>
              YT cookies 失效
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            退出
          </Button>
        </div>
      </header>
      <main className="flex-1 overflow-hidden bg-muted/30 print:h-auto print:overflow-visible">{children}</main>
    </div>
  );
}
